import { FILE_URL } from 'config/app.config';

export const FFL_BANNER_MIN_WIDTH = 600;
export const FFL_BANNER_MIN_HEIGHT = 200;
export const ALLOW_MAX_FILE_SIZE = 5; // MB
export const ALLOWED_PDF_TYPES = ['application/pdf'];
export const ALLOWED_IMAGES_TYPES = ['image/jpeg', 'image/png'];
export const AllOWED_CSV_TYPES = [
  'application/csv',
  'text/csv',
  'text/plain',
  'text/comma-separated-values',
];
export const PROFILE_IMAGE_FOLDER = 'profile';
export const ALBUM_IMAGE_FOLDER = 'album';
export const CATEGORY_IMAGE_FOLDER = 'category';
export const MANUFACTURER_IMAGE_FOLDER = 'manufacturer';
export const HOME_BANNER_IMAGE_FOLDER = 'banner';
export const HOME_BANNER_MIN_WIDTH = 1200;
export const HOME_BANNER_MIN_HEIGHT = 400;
export const FFL_LOGO_FOLDER = 'ffl/logo';
export const FFL_BANNER_FOLDER = 'ffl/banner';
export const FFL_LICENSE_FOLDER = 'ffl/license';

export const BLOG_BANNER_MIN_WIDTH = 800;
export const BLOG_BANNER_MIN_HEIGHT = 400;
export const BLOG_IMAGE_FOLDER = 'blogs';
export const CONTENT_PAGE_IMAGE_FOLDER = 'content_page';

export const PRODUCT_MIN_WIDTH = 560;
export const PRODUCT_MIN_HEIGHT = 609;
export const PRODUCT_IMAGE_FOLDER = 'product';
export const PRODUCT_FEATURED_IMAGE_MIN_WIDTH = 600;
export const PRODUCT_FEATURED_IMAGE_MIN_HEIGHT = 200;
export const PRODUCT_ALLOW_MAX_FILE_SIZE = 20; // MB
export const IMAGE_SIZE = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
};

export const MESSAGE_IMAGE_FOLDER = 'message';
export type ImageSizeType = (typeof IMAGE_SIZE)[keyof typeof IMAGE_SIZE];
export const getImageURL = (path: string, size?: ImageSizeType) => {
  if (!path || path === 'null' || path === 'undefined') return '';
  if (size) {
    return `${FILE_URL}${path}?size=${size}`;
  } else {
    return `${FILE_URL}${path}`;
  }
};
// Shape an already-uploaded image path into the file object GlobalImageUpload renders,
// and that the submit handlers read back via .imagePath. Lives here rather than in that
// component because some pages seed the form fields directly instead of handing the paths
// over as existingFeaturedImage / existingGallery props - both routes have to produce the
// same shape or the thumbnails render blank.
export const buildExistingImageFile = (url: string, i: number) => ({
  uid: `existing-${i}-${Date.now()}`,
  name: `image-${i}`,
  status: 'done',
  imagePath: url,
  url: getImageURL(url),
  thumbUrl: getImageURL(url, IMAGE_SIZE.sm),
});

// Saves a PDF the API returned as a real file download. window.open() after an await is
// swallowed by popup blockers (the user's click is long gone by the time a slow PDF comes back),
// so nothing seemed to happen; a download link is not subject to that.
export const downloadPdfFromBuffer = (buffer: ArrayBuffer, fileName: string) => {
  const fileURL = URL.createObjectURL(new Blob([buffer], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = fileURL;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
};

export const downloadBufferDataFile = (dataObj: any) => {
  const blob = new Blob([dataObj.bufferData], { type: dataObj.fileType });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = dataObj.fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const AD_LP_MIN_WIDTH = 900;
export const AD_LP_MIN_HEIGHT = 275;
export const AD_TB_MIN_WIDTH = 675;
export const AD_TB_MIN_HEIGHT = 150;
export const AD_MO_MIN_WIDTH = 297;
export const AD_MO_MIN_HEIGHT = 100;
export const AD_VT_MIN_WIDTH = 240;
export const AD_VT_MIN_HEIGHT = 600;
export const AD_IMAGE_FOLDER = 'advertisement';
export const REVIEWS_IMAGE_FOLDER = 'reviews';
export const RETURN_REQUEST_IMAGE_FOLDER = 'return_requests';
export const ALLOWED_IMAGES_AND_VIDEO_TYPES = [
  ...ALLOWED_IMAGES_TYPES,
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/quicktime',
];

export const isVideoFile = (url: string) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.quicktime'];
  const urlWithoutParams = url.split('?')[0];
  return videoExtensions.some((ext) => urlWithoutParams.toLowerCase().endsWith(ext));
};

export const isImageFile = (nameOrUrl: string) => {
  if (!nameOrUrl) return false;
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(nameOrUrl.split('?')[0]);
};

export const isPdfFile = (nameOrUrl: string) => {
  if (!nameOrUrl) return false;
  return /\.pdf$/i.test(nameOrUrl.split('?')[0]);
};

export const getFileNameFromPath = (path: string) => {
  if (!path) return '';
  const name = path.split('?')[0].split('/').pop() || '';
  try {
    return decodeURIComponent(name);
  } catch {
    // a literal '%' in the file name is not a valid escape sequence
    return name;
  }
};

export const formatFileSize = (bytes?: number) => {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const size = bytes / Math.pow(1024, exponent);
  return `${size >= 10 || exponent === 0 ? Math.round(size) : size.toFixed(1)} ${units[exponent]}`;
};

/**
 * Saves a remote file to disk. The media server sits on a different origin than the app, so a plain
 * `<a download>` is ignored there — fetching the bytes first is what actually saves the file. If that
 * fetch is blocked (CORS), the file is opened instead so the user can still save it manually.
 */
export const downloadFileFromURL = async (url: string, fileName?: string) => {
  if (!url) return false;

  const triggerDownload = (href: string) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = fileName || getFileNameFromPath(url) || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (url.startsWith('blob:') || url.startsWith('data:')) {
    triggerDownload(url);
    return true;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('download_failed');
    const blobURL = URL.createObjectURL(await response.blob());
    triggerDownload(blobURL);
    URL.revokeObjectURL(blobURL);
    return true;
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
    return false;
  }
};
